export const MAP_STYLE_WHITE: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#fafaf8" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6b7280" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#fafaf8" }, { weight: 3 }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#d6dade" }] },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.neighborhood", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#4b5563" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#e8eaec" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#d8dce0" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#8a939c" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#dde6eb" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#93a3ae" }] },
];

/**
 * El mapa en oscuro: la carta de navegación del HUD.
 *
 * ── Qué problema resuelve ───────────────────────────────────────────────────
 * No había estilo oscuro. Con el tema en oscuro, la app quedaba con paneles
 * casi negros alrededor de un mapa blanco de Google: el contraste partía la
 * pantalla en dos y la vista se iba al rectángulo luminoso, no a las unidades.
 *
 * ── Cómo está construido ────────────────────────────────────────────────────
 * El suelo es apenas MÁS CLARO que los paneles (#070C14 contra #0C131E a la
 * inversa: el mapa es el plano de fondo y los paneles flotan encima). Las vías
 * se dibujan como una red de luz tenue —relleno oscuro, trazo claro— porque en
 * un mapa de operaciones lo que importa es la FORMA de la red, no su color.
 *
 * El agua es más oscura que la tierra. Es al revés de lo habitual y es a
 * propósito: en un HUD el agua es vacío, y el vacío no brilla.
 *
 * ── Dónde falla este estilo, y por qué no falla aquí ────────────────────────
 * Los HUD oscuros se rompen en las etiquetas: se ponen tenues «porque se ve
 * mejor» y dejan de leerse. Aquí el texto va en #9FB3CA sobre el suelo —más de
 * 7:1— con un halo oscuro de 3 px debajo, que es la técnica de siempre en
 * cartografía para que una etiqueta sobreviva encima de cualquier cosa. Las
 * localidades suben a #C8D7E8 porque son el ancla para ubicarse de un vistazo.
 */
export const MAP_STYLE_DEEP: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#070C14" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9FB3CA" }] },
  // El halo. Sin esto una etiqueta sobre una autopista clara desaparece.
  { elementType: "labels.text.stroke", stylers: [{ color: "#04070D" }, { weight: 3 }] },

  // Fronteras: la retícula del mapa. Presentes, nunca protagonistas.
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#1C2A3C" }] },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.neighborhood", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#C8D7E8" }] },
  { featureType: "administrative.province", elementType: "labels.text.fill", stylers: [{ color: "#6B7F99" }] },
  { featureType: "administrative.country", elementType: "labels.text.fill", stylers: [{ color: "#8FA3BC" }] },

  // Los puntos de interés estorban: el mapa es para seguir unidades.
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "landscape.man_made", elementType: "geometry", stylers: [{ color: "#0A1018" }] },

  // Las vías, como una red de luz tenue.
  { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#121C29" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#1A2636" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#7D91AB" }] },
  { featureType: "road.arterial", elementType: "geometry.fill", stylers: [{ color: "#16222F" }] },
  // La autopista es lo único que sube de brillo: es por donde van los viajes.
  { featureType: "road.highway", elementType: "geometry.fill", stylers: [{ color: "#1D2E40" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#27405A" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#A8BCD4" }] },

  { featureType: "transit", stylers: [{ visibility: "off" }] },

  // El agua, más oscura que la tierra: en un HUD el vacío no brilla.
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#03060B" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4A5C73" }] },
];

/**
 * Qué estilo toca.
 *
 * Los tipos con foto —satélite e híbrido— no llevan estilo: pintarlos encima
 * sólo ensucia la imagen, que es justo lo que se vino a ver.
 *
 * `esOscuro` es opcional para no romper a quien ya llamaba con un argumento;
 * esos siguen recibiendo el mapa claro, igual que antes.
 */
export const getMapStyles = (
  mapTypeId?: string | null,
  esOscuro = false,
): google.maps.MapTypeStyle[] | undefined => {
  if (mapTypeId && mapTypeId !== "roadmap") return undefined;
  return esOscuro ? MAP_STYLE_DEEP : MAP_STYLE_WHITE;
};
